import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiMail, FiLinkedin, FiGithub, FiCopy, FiCheck } from 'react-icons/fi';

const ContactSection = styled.section`
  padding: 100px 150px;
  background-color: var(--bg-secondary);
  
  @media (max-width: 768px) {
    padding: 80px 25px;
  }
`;

const ContactContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const ContactTitle = styled(motion.h2)`
  display: flex;
  align-items: center;
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 40px;
  color: var(--text-primary);
  
  &::after {
    content: "";
    display: block;
    width: 300px;
    height: 1px;
    margin-left: 20px;
    background-color: var(--bg-highlight);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    
    &::after {
      width: 100px;
    }
  }
`;

const ContactDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 18px;
  margin-bottom: 60px;
  max-width: 700px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContactCardWrapper = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: 60px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--bg-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  box-shadow: 0 10px 25px rgba(2, 12, 27, 0.2);
  border: 1px solid var(--bg-highlight);
  transition: all 0.3s ease;
  
  svg {
    font-size: 35px;
    color: var(--accent-primary);
  }
`;

const ContactCard = styled(motion.div)`
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 40px 20px;
  width: 340px;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  border: 1px solid var(--bg-highlight);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px -20px rgba(2, 12, 27, 0.7);
    border-color: var(--accent-primary);
    
    ${IconWrapper} {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(100, 255, 218, 0.3);
      border-color: var(--accent-primary);
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 340px;
  }
`;

const ContactCardTitle = styled.h3`
  color: var(--text-primary);
  font-size: 22px;
  margin-bottom: 15px;
`;

const ContactCardLink = styled.a`
  color: var(--text-secondary);
  transition: color 0.2s ease-in-out;
  font-size: 13px;
  display: block;
  white-space: normal;
  overflow: visible;
  width: 100%;
  max-width: 100%;
  text-align: center;
  position: relative;
  cursor: pointer;
  background-color: transparent;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 500;
  border: 1px solid var(--bg-highlight);
  margin: 0 auto;
  line-height: 1.4;
  word-break: normal;
  overflow-wrap: break-word;
  font-family: var(--font-mono);
  letter-spacing: -0.2px;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -5px;
    left: 0;
    right: 0;
    margin: 0 auto;
    background-color: var(--accent-primary);
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: var(--accent-primary);
    background-color: rgba(100, 255, 218, 0.05);
    border-color: var(--accent-primary);
    
    &:after {
      width: 100%;
    }
  }
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  padding: 8px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  border: 1px solid var(--bg-highlight);
  
  svg {
    margin-right: 5px;
  }
  
  &:hover {
    color: var(--accent-primary);
    background: rgba(100, 255, 218, 0.1);
    border-color: var(--accent-primary);
  }
  
  &.copied {
    color: var(--accent-primary);
    background: rgba(100, 255, 218, 0.1);
    border-color: var(--accent-primary);
  }
`;

const CopyFeedback = styled(motion.div)`
  position: fixed;
  bottom: 20px;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: max-content;
  max-width: 90%;
  background-color: var(--bg-primary);
  color: var(--accent-primary);
  padding: 10px 20px;
  border-radius: 4px;
  border: 1px solid var(--accent-primary);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  text-align: center;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.2,
      duration: 0.5
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration:.5,
      type: "spring",
      stiffness: 100
    }
  }
};

const Contact = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [descRef, descInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [cardsRef, cardsInView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  const [copyStatus, setCopyStatus] = useState({
    email: false,
    linkedin: false,
    github: false
  });
  
  const [feedback, setFeedback] = useState({
    show: false,
    text: ''
  });
  
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        // Set the specific button to copied state
        setCopyStatus({
          ...copyStatus,
          [type]: true
        });
        
        // Show feedback
        setFeedback({
          show: true,
          text: `Copied: ${text}`
        });
        
        // Reset feedback after 2 seconds
        setTimeout(() => {
          setFeedback({
            show: false,
            text: ''
          });
        }, 2000);
        
        // Reset button state after 1.5 seconds
        setTimeout(() => {
          setCopyStatus({
            ...copyStatus,
            [type]: false
          });
        }, 1500);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        setFeedback({
          show: true,
          text: 'Failed to copy. Please try again.'
        });
        
        setTimeout(() => {
          setFeedback({
            show: false,
            text: ''
          });
        }, 2000);
      });
  };
  
  return (
    <ContactSection id="contact">
      <ContactContainer>
        <ContactTitle
          ref={titleRef}
          initial={{ y: 20, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          Get In Touch
        </ContactTitle>
        
        <ContactDescription
          ref={descRef}
          initial={{ y: 20, opacity: 0 }}
          animate={descInView ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          I'm currently open to new opportunities in the cybersecurity field. 
          Whether you have a question or just want to say hi, I'll do my best to get back to you!
        </ContactDescription>
        
        <ContactCardWrapper
          ref={cardsRef}
          variants={containerVariants}
          initial="hidden"
          animate={cardsInView ? "visible" : "hidden"}
        >
          <ContactCard variants={itemVariants} whileHover={{ y: -10 }}>
            <IconWrapper>
              <FiMail />
            </IconWrapper>
            <ContactCardTitle>Email</ContactCardTitle>
            <ContactCardLink href="mailto:nandanyadavonline@gmail.com" title="nandanyadavonline@gmail.com">
              nandanyadavonline@gmail.com
            </ContactCardLink>
            <CopyButton 
              onClick={() => copyToClipboard('nandanyadavonline@gmail.com', 'email')}
              className={copyStatus.email ? 'copied' : ''}
            >
              {copyStatus.email ? <FiCheck size={14} /> : <FiCopy size={14} />} 
              {copyStatus.email ? 'Copied!' : 'Copy'}
            </CopyButton>
          </ContactCard>
          
          <ContactCard variants={itemVariants} whileHover={{ y: -10 }}>
            <IconWrapper>
              <FiLinkedin />
            </IconWrapper>
            <ContactCardTitle>LinkedIn</ContactCardTitle>
            <ContactCardLink 
              href="https://linkedin.com/in/nandantrsn" 
              target="_blank" 
              rel="noopener noreferrer"
              title="linkedin.com/in/nandantrsn"
            >
              linkedin.com/in/nandantrsn
            </ContactCardLink>
            <CopyButton 
              onClick={() => copyToClipboard('https://linkedin.com/in/nandantrsn', 'linkedin')}
              className={copyStatus.linkedin ? 'copied' : ''}
            >
              {copyStatus.linkedin ? <FiCheck size={14} /> : <FiCopy size={14} />} 
              {copyStatus.linkedin ? 'Copied!' : 'Copy'}
            </CopyButton>
          </ContactCard>
          
          <ContactCard variants={itemVariants} whileHover={{ y: -10 }}>
            <IconWrapper>
              <FiGithub />
            </IconWrapper>
            <ContactCardTitle>GitHub</ContactCardTitle>
            <ContactCardLink 
              href="https://github.com/L337C0D3R" 
              target="_blank" 
              rel="noopener noreferrer"
              title="github.com/L337C0D3R"
            >
              github.com/L337C0D3R
            </ContactCardLink>
            <CopyButton 
              onClick={() => copyToClipboard('https://github.com/L337C0D3R', 'github')}
              className={copyStatus.github ? 'copied' : ''}
            >
              {copyStatus.github ? <FiCheck size={14} /> : <FiCopy size={14} />} 
              {copyStatus.github ? 'Copied!' : 'Copy'}
            </CopyButton>
          </ContactCard>
        </ContactCardWrapper>
        
        {feedback.show && (
          <CopyFeedback
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            {feedback.text}
          </CopyFeedback>
        )}
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact; 